/**
 * useNotificationActions
 *
 * The single, canonical write path for notification mutations. Both the header
 * bell/dropdown (via `useNotifications`) and the full inbox page (via
 * `useNotificationsPage`) route their read/unread/delete actions through here,
 * so a notification marked read in one surface is reflected in the other and is
 * always persisted to the server.
 *
 * Each action is optimistic against the live store (so the badge and dropdown
 * update instantly), persists to the REST API, and rolls the store change back
 * on failure with a toast. Actions return a promise that rejects on failure so
 * callers with their own optimistic UI (the page's REST-list overlay) can roll
 * back too; fire-and-forget callers can ignore it.
 */

import { useCallback } from 'react';

import { notificationService } from '@personal-trainer-app/shared/src/services';

import { useToast } from '@/components/ui/Toast';
import { useNotificationStore } from '@/stores';

export interface UseNotificationActionsReturn {
  markAsRead: (id: string) => Promise<void>;
  markAsUnread: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  /**
   * Retire a notification that has done its job — an invitation that was just
   * answered. The same removal as a delete, without announcing it: the user
   * answered a question, they did not delete anything.
   */
  resolveNotification: (id: string) => Promise<void>;
}

export function useNotificationActions(): UseNotificationActionsReturn {
  const { success, error: toastError } = useToast();

  const storeMarkAsRead = useNotificationStore((s) => s.markAsRead);
  const storeMarkAsUnread = useNotificationStore((s) => s.markAsUnread);
  const storeMarkAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const storeRemove = useNotificationStore((s) => s.removeNotification);
  const storeAdd = useNotificationStore((s) => s.addNotification);

  const markAsRead = useCallback(
    async (id: string): Promise<void> => {
      storeMarkAsRead(id);
      try {
        await notificationService.markAsRead(id);
      } catch (err) {
        storeMarkAsUnread(id);
        toastError('Could not mark as read. Please try again.');
        throw err;
      }
    },
    [storeMarkAsRead, storeMarkAsUnread, toastError]
  );

  const markAsUnread = useCallback(
    async (id: string): Promise<void> => {
      storeMarkAsUnread(id);
      try {
        await notificationService.markAsUnread(id);
      } catch (err) {
        storeMarkAsRead(id);
        toastError('Could not mark as unread. Please try again.');
        throw err;
      }
    },
    [storeMarkAsRead, storeMarkAsUnread, toastError]
  );

  const markAllAsRead = useCallback(async (): Promise<void> => {
    // Snapshot the ids that were unread in the live window so we can restore
    // their read state if the request fails. The badge count is restored only
    // for the in-window items; a reseed/reload reconciles it with the server.
    const previouslyUnread = useNotificationStore
      .getState()
      .getUnreadNotifications()
      .map((n) => n.id);

    storeMarkAllAsRead();
    try {
      await notificationService.markAllAsRead();
    } catch (err) {
      previouslyUnread.forEach((id) => storeMarkAsUnread(id));
      toastError('Could not mark all as read. Please try again.');
      throw err;
    }
  }, [storeMarkAllAsRead, storeMarkAsUnread, toastError]);

  const deleteNotification = useCallback(
    async (id: string): Promise<void> => {
      const removed = useNotificationStore.getState().getNotificationById(id);

      storeRemove(id);
      try {
        await notificationService.remove(id);
        success('Notification deleted');
      } catch (err) {
        if (removed) storeAdd(removed);
        toastError('Could not delete notification. Please try again.');
        throw err;
      }
    },
    [storeRemove, storeAdd, success, toastError]
  );

  const resolveNotification = useCallback(
    async (id: string): Promise<void> => {
      storeRemove(id);
      // Not rolled back on failure: the answer already landed, so the worst
      // case is a spent row reappearing on the next load, where answering it
      // again is refused by the API.
      await notificationService.remove(id).catch(() => undefined);
    },
    [storeRemove]
  );

  return {
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    resolveNotification,
  };
}

export default useNotificationActions;
